// @flow
import moment from 'moment';
import { parseCurrency, round } from './helpers';
import {
  defaultCommercialInterestRate,
  defaultAdminFeeRate,
  defaultInsuranceFeeRate
} from './constants';
import type { Rows, Stats, Simulation } from './types';

/**
 * Simulador de Préstamos Comerciales.
 * Usamos el sistema de interés Francés y pagos semestrales.
 * Detalles del cálculo en la Wiki.
 *
 * @param {number} amount - capital, el monto del préstamo solicitado por el cliente
 * @param {number} duration - cantidad de cuotas o plazo del préstamo, en semestres
 * @param {number} interestRate - tasa de interés mensual para préstamos comerciales
 * @param {number} adminFeeRate - porcentaje del capital que se cobra como gastos administrativos
 * @param {number} insuranceFeeRate - porcentaje del capital que se cobra en concepto de seguro
 */

export default function commercial(
  amount: number,
  duration: number,
  interestRate: number = defaultCommercialInterestRate,
  adminFeeRate: number = defaultAdminFeeRate,
  insuranceFeeRate: number = defaultInsuranceFeeRate
): Simulation {
  // Los valores a continuación son independientes del período,
  // i.e.: constantes para todas las filas de la tabla.

  // Frecuencia
  // Cantidad de meses abarcados en cada cuota
  // -- Mensual = 1
  // -- Trimestral = 3
  // -- Cuatrimestral = 4
  // -- Semestral = 6
  const freq: 1 | 3 | 4 | 6 = 6;

  // Interés (TEM para la frecuencia elegida)
  // TEM = commercialInterestRate = 3.5%
  // pero si la frecuencia es != 1 necesitamos recalcularlo.
  const interest: number = Math.pow(1 + interestRate, freq) - 1;

  // Amortización
  const principalPartialResult: number = Math.pow(1 + interest, duration);
  const principal: number =
    (interest * principalPartialResult) / (principalPartialResult - 1);

  // Valor de la cuota pura
  const payment: number = amount * principal;

  // Gastos administrativos para cada semestre, 1% semestral
  const adminFee: number = (amount * adminFeeRate) / duration;

  // Seguro para cada semestre, 0.1% mensual
  // El valor por defecto es mensual, para hacerlo semestral
  // lo multiplicamos por seis: 0.1% mensual * 6 = 0.6% semestral
  const insuranceFee: number = amount * insuranceFeeRate * freq;

  // Valor final de la cuota para cada semestre
  // Incluye gastos administrativos y seguro
  const totalPayment: number = payment + adminFee + insuranceFee;

  // Devengamientos total
  const accrued: number = payment * duration - amount;

  // Total a pagar por el préstamo
  // Capital solicitado + Devengados + Gastos administrativos + Seguro
  const grandTotal: number =
    amount + accrued + duration * (adminFee + insuranceFee);

  // Temporary values and partial sums
  let accumulatedPrincipal: number = 0; // Total amortizado a la fecha
  let balance: number = grandTotal; // Deuda a la fecha

  // Los valores a continuación son función del número de cuota,
  // por lo tanto son diferentes para todas las filas de la tabla.
  const rows: Rows = Array(duration)
    .fill(0)
    .map((row: number, index: number) => {
      // Número de cuota
      const term: number = index + 1;

      // Vencimiento, los pagos son semestrales, por eso contamos de 6 en 6 meses
      const dueDate: string = moment()
        .add(freq * (index + 1), 'months')
        .format('DD/MM/YYYY');

      // Interés del período
      const rowInterest: number = (amount - accumulatedPrincipal) * interest;

      // Amortización del período
      const rowPrincipal: number = payment - rowInterest;

      // Total amortizado a la fecha
      accumulatedPrincipal += rowPrincipal;

      // Deuda a la fecha
      const rowBalance: number = balance;

      balance -= totalPayment;

      return [
        term,
        dueDate,
        parseCurrency(rowBalance),
        parseCurrency(rowPrincipal),
        parseCurrency(rowInterest),
        parseCurrency(adminFee),
        parseCurrency(insuranceFee),
        parseCurrency(totalPayment),
        parseCurrency(accumulatedPrincipal)
      ];
    });

  const stats: Stats = {
    type: 'Francés',
    amount: parseCurrency(amount),
    accrued: parseCurrency(accrued),
    duration: `${duration} semestres`,
    nominalInterestRate: `${round(interestRate * 12 * 100)}%`,
    monthlyInterestRate: `${round(interestRate * 100)}%`,
    stateTax: parseCurrency(amount * 0.012),
    actual: parseCurrency(amount - amount * 0.012),
    admin: parseCurrency(adminFee * duration),
    insurance: parseCurrency(insuranceFee * duration),
    averagePayment: parseCurrency(totalPayment),
    total: parseCurrency(grandTotal)
  };

  return {
    stats,
    rows
  };
}
